// src/components/ViewHeader.jsx
import * as React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Stack,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { PageContainerContext, PageWidthContext, PAGE_GUTTER } from './common/PageContainer';
import ProgressButton from './common/ProgressButton';
import RefreshButton from './common/RefreshButton';
import SplitButton from './common/SplitButton';
import { menueFarbe } from './common/menueFarbe';
import { useTableWidth } from './common/tableWidth';
import { CONTENT_WIDTH, COLUMN_PRIORITY, CONTROL_HEIGHT_SX, SP, TOUCH_TARGET_COARSE_QUADRAT } from '../theme/tokens';

/**
 * Die STUFENLEITER des Kopf-Deckels (BW3, G0b.4).
 *
 * 🔴 Das Konzept definiert genau **einen** Begriff „Stufe": die Schwellen der
 * Spalten-Prioritaet (`inhaltsbreite.md` §3.2.2:442-448, `kern`/`lg`/`xl`/`xxl`).
 * Der Kopf uebernimmt eine gemessene Breite *„in Schritten von hoechstens einer
 * Stufe je Frame"* (NW4.6:652, §4.3:617-619, Pruefung **PW3b**:1005) — also
 * wandert sein Deckel ueber **diese** Leiter und nicht ueber eine erfundene.
 *
 * Sie traegt die drei optionalen Schwellen (`kern` = 0 und `frei` = `Infinity`
 * sind keine Deckel) **plus** `CONTENT_WIDTH.reading` — den ersten Anstrich
 * (§4.3:614-616). Aufsteigend, ohne Doppelte. ⚠️ *Abgeleitet, nicht
 * Owner-Wortlaut:* die Leiter selbst ist im Konzept nicht ausgeschrieben; sie
 * ist die einzige „Stufe", die es definiert.
 */
const STUFENLEITER = Array.from(
  new Set([CONTENT_WIDTH.reading, COLUMN_PRIORITY.lg, COLUMN_PRIORITY.xl, COLUMN_PRIORITY.xxl]),
).sort((a, b) => a - b);

/** Eine Stufe naeher am Ziel — **hoechstens eine je Frame** (NW4.6:652, PW3b:1005).
 *
 *  🔴 Warum ueberhaupt gedeckelt: *„nie ein Sprung von `reading` auf das Budget in
 *  einem Bild"* (§4.3:618-619). Ein Kopf, der bei 2560 in EINEM Frame von 1280 auf
 *  2184 springt, ist eine sichtbare Zuckung.
 *
 *  Zwischen den Sprossen faellt der Deckel auf das Ziel durch (es gibt keine
 *  Stufe mehr, die noch dazwischen liegt); ausserhalb der Leiter (kleiner als
 *  1024 oder groesser als 1728) ist der letzte Schritt ebenfalls das Ziel. */
export function naechsteStufe(von, ziel) {
  if (von === ziel) return ziel;
  if (ziel > von) {
    const hoeher = STUFENLEITER.find((stufe) => stufe > von);
    return hoeher == null || hoeher > ziel ? ziel : hoeher;
  }
  const tiefer = [...STUFENLEITER].reverse().find((stufe) => stufe < von);
  return tiefer == null || tiefer < ziel ? ziel : tiefer;
}

// 🔴 W2-Nachbesserung (2026-10-07): `color` kommt am Ueberlauf in ZWEI Formen,
// und nur EINE davon war je richtig. Der Ueberlauf selbst reicht PFADE
// (`'error.main'`, `RMADetail`), Aktionen dagegen nackte Palettenschluessel
// (`'warning'` fuer „Deduplicate" in `ScraperList`). Ein nackter Schluessel ist
// KEIN CSS-Wert: in `sx` loest `color` gegen `theme.palette` auf, `'warning'`
// also gegen das PALETTENOBJEKT `{ main, light, dark, contrastText }` — die
// Deklaration ist damit ungueltig und die Farbe kommt nie an. Der Warnhinweis
// war unsichtbar.
//
// 🔴 E3 (UI-Durchgang 2 Teil 4, 2026-10-08): die Abbildung, die hier als
// `ueberlaufFarbe` stand, liegt seit dieser Runde EINMAL im Baum —
// `./common/menueFarbe`. Die hier gefuehrte Begruendung fuer die DOPPLUNG
// („`ViewHeader` importiert `SplitButton`, ein Import in die Gegenrichtung waere
// ein Zyklus") bleibt richtig und trifft das neue Modul nicht: die Kante ist
// einbahnig `ViewHeader → SplitButton`, und ein drittes Modul, das beide
// importieren, liegt auf keiner Kante. Was `menueFarbe` sonst noch von der
// frueheren Fassung unterscheidet — der Zielpfad ist jetzt der TEXT-TON der
// v2-Skala statt `<schluessel>.main`, weil `warning.main` hell 3,113:1 und
// `info.main` hell 3,857:1 messen —, steht im Kopf jenes Moduls.

/**
 * W-13 (Wiedervorlage 1, 2026-10-07): die Kopfzeile ist EINE Knopfzeile.
 *
 * Der Aktionsknopf traegt nur `size="small"` und misst damit seine natuerliche
 * Hoehe (gerechnet ≈ 30 px, `theme.js` gibt ihm unter `sm` 44 und darueber
 * nichts) — waehrend das Select im `rightAdornment` daneben aus C
 * `CONTROL_HEIGHT_SX` bekommt (32 px). Zwei Bedienelemente DERSELBEN Zeile mit
 * verschiedener Hoehe; das ist der Fotobefund („Status-Select ~40 px neben
 * 'Edit' 28 px"). Diese Regel zieht die Knoepfe auf denselben Boden.
 *
 * 🔴 Als NACHFAHREN-Regel am `Stack` statt als `sx` je Knopf, und der Grund ist
 * gemessen: `ProgressButton` setzt sein `sx` intern und spreadet die
 * Aufrufer-Props DANACH (`ProgressButton.jsx:145-152`) — ein von aussen
 * gereichtes `sx` ERSETZT dort `position: relative` und `overflow: hidden` und
 * zerlegt den Fortschrittsbalken. `SplitButton` kennt gar kein `sx`. Ein
 * `sx` je Zweig waere also dreimal dieselbe Zeile mit zwei Sonderfaellen.
 *
 * 🔴 Getroffen wird `MuiButton-root` — der einfache `Button` UND die zwei
 * Haelften des `SplitButton` (beide sind `MuiButton-root`). NICHT getroffen:
 * das Select im `rightAdornment` (`MuiInputBase-root`; C gibt ihm dieselbe
 * Hoehe) und der portierte `Menu`-Inhalt (er haengt am `body`, nicht am Stack).
 *
 * 🔴 F-3 (R16 W-1, 2026-10-08): der ⋯-Ueberlauf zieht auf DENSELBEN Boden. Bis
 * dahin war er ausgenommen und maß ~30 px — 2 px unter der Zeile, die er
 * bedient. Quadratisch, weil `MuiIconButton` `borderRadius: '50%'` traegt und
 * `height` ALLEIN ihn zum Oval machte; deshalb BEIDE Achsen.
 *
 * 🔴 Die zwei Achsen tragen den Wert aus dem Token, nicht ein Literal:
 * `CONTROL_HEIGHT_SX.height.sm` ist der 32er-Boden der Knopfzeile. Dazu der
 * grobe Zeiger-Boden `TOUCH_TARGET_COARSE_QUADRAT` (dieselbe Hausform wie der
 * ▾-Knopf des `SplitButton`, W-3) — er ist hier NICHT entbehrlich: diese Regel
 * ist eine NACHFAHREN-Regel und damit spezifischer als der Theme-Override
 * `MuiIconButton.root`, dessen `@media (pointer: coarse)`-Boden (44) sonst von
 * der 32 unterlaufen wuerde. Nur der DESKTOP-Zweig traegt diese Regel; unter
 * `sm` bleibt der Theme-Boden (44) unveraendert.
 */
const KOPFZEILE_HOEHE = {
  '& .MuiButton-root': { ...CONTROL_HEIGHT_SX },
  '& .MuiIconButton-root': {
    minHeight: CONTROL_HEIGHT_SX.height.sm,
    minWidth: CONTROL_HEIGHT_SX.height.sm,
    ...TOUCH_TARGET_COARSE_QUADRAT,
  },
};

/**
 * ViewHeader
 * - Desktop: breadcrumbs + H1 left, up to 3 actions right (side-by-side)
 * - Mobile:  H1 full-width on its own row, actions in a second row below
 *            (flex-wrap so they never overflow; subtitle shown under title)
 *            🔴 ZWEI AUSNAHMEN, in denen diese zweite Zeile ENTFAELLT und der
 *            Aktionsbereich rechts in die TITELZEILE wandert (F12/T3-15 und
 *            T3-3/F3, beide 2026-10-08) — in beiden stuende sonst eine eigene
 *            Zeile mit genau EINEM Bedienelement darin:
 *              (a) der ⋯ traegt den Bereich als EINZIGES (`onlyOverflow`) —
 *                  sonst ein einsamer ⋮;
 *              (b) die sichtbaren Aktionen sind ALLE `iconOnly` (der
 *                  Auffrisch-Knopf) — sonst ein einsames Symbol.
 *            🔴 Ein LEERER Aktionsbereich ist KEIN Fall (b). „Noch nichts da"
 *            ist etwas anderes als „alles Symbole": den ersten Fall traegt
 *            `actionsPending` (`reserveActionsRow`), und ihn unter (b) zu
 *            ziehen risse die reservierte Zeile auf jedem ladenden Kopf weg
 *            (R15 W4 der Runde 1). Deshalb verlangt (b) `visible.length > 0`.
 * - Children: optional filter row rendered beneath everything
 *
 * 🔴 E4 (UI-Durchgang 2, 2026-10-07): EINE Reihenfolge auf BEIDEN Breiten —
 * `[rightAdornment] [sekundär …] [primär] [⋯]`. Die primäre Aktion steht
 * rechts aussen vor dem Überlauf, unabhängig von ihrer Position im Array; vor
 * dieser Änderung rendete der Desktop `rightAdornment` VOR den Aktionen, der
 * Mobil-Zweig DANACH.
 *
 * 🔴 E5: unter `sm` steht inline nur die PRIMÄRE Aktion (E2), alles Weitere
 * geht in den Überlauf — AUSSER der Kopf trägt `stickyOnMobile`: dann bleiben
 * alle Aktionen inline (Bearbeiten-Köpfe, in denen Cancel und Save die
 * Aktionen sind, für die es den Schalter gibt). Der mobile Überlauf ist ein
 * `Menu`, kein `ActionSheet` (dessen API trägt keine Eintragsliste).
 *
 * 🔴 E-W5 (2026-10-06): der Kopf liest `PageContainerContext` und `PAGE_GUTTER`
 * aus `common/PageContainer.jsx`. INNERHALB eines `PageContainer` traegt er
 * `px: 0` (sonst addierte sich sein Puffer auf den des Containers, der Titel
 * saesse 24 px weiter innen); DANEBEN liefert er den Inset selbst
 * (`px: PAGE_GUTTER`). Es muss ein EXPLIZITES `px` bleiben — der
 * `MuiContainer`-Override setzt `paddingLeft/Right` auf jedem Container, ein
 * blosses Weglassen liesse ihn stehen.
 *
 * 🔴 **KM-1 (2026-10-10): EIN Seitenkopf zeigt höchstens EIN Menü.** Trägt eine
 * inline stehende Aktion ein `menu` UND hat der Kopf ⋯-Einträge, dann wird die
 * Aktion zum einfachen Knopf und ihre Varianten stehen oben im ⋯. Die Regel
 * gilt für **Seitenköpfe**; **Kartenköpfe sind nicht erfasst** (sie tragen
 * keinen `ViewHeader` — z. B. der direkte `SplitButton` im Kopf der
 * Preisautomatik, `PricingAutomationDashboard.jsx`, neben einem
 * `RefreshButton` und ohne ⋯).
 *
 * @useWhen
 *   - Die Kopfleiste einer Seite mit Aktionen (`/products`, `/rma`, `/vendors/:id`, …)
 *   - Eine Hauptaktion mit seltenen Geschwistern derselben Sache (`menu: [...]`)
 *   - Ein Kopf, dessen einzige Aktion ein Neuladen ist (`iconOnly`)
 *   - Ein Kopf mit Statusanzeige hinter dem Titel (`titleAdornment`)
 *
 * @dontUseFor
 *   - Ein Abschnitt INNERHALB einer Seite — dafür Überschrift + `Panel`
 *   - Dialog- oder Sheet-Köpfe — dafür `DialogTitle` bzw. `MobileSheet`
 *   - Eine zweite Kopfleiste auf derselben Seite (zwei `h1`)
 *   - **Ein Kartenkopf** — dort ist ein `SplitButton` mit einem
 *     Auffrisch-Knopf daneben zulässig; die Ein-Menü-Regel gilt für Seitenköpfe
 *   - Ein `menu` **neben** einem ⋯ — der Kopf zerlegt es dann selbst (KM-1);
 *     wer zwei Menüs will, will sie nicht
 *
 * @related
 *   - `PageContainer` — der Rahmen, den der Kopf deckungsgleich bedient; er
 *     stellt beide Kontexte (`PageContainerContext`, `PageWidthContext`)
 *   - `PAGE_GUTTER` — der Rand, den der Kopf nur DANEBEN selbst liefert
 *   - `CONTENT_WIDTH` (`theme/tokens.js`) — die eine Quelle des Deckels
 *   - 🔴 Eine Seite, deren Kopf **neben** (nicht in) einem `PageContainer` steht
 *     und die `wide`/`full` traegt, legt `<PageWidthContext.Provider value="…">`
 *     um Kopf **und** Container — sonst gilt fuer den Kopf die Vorgabe `reading`
 *     (1280) und er stuende ueber breiterem Inhalt. Betrifft die sieben
 *     Geschwister-Seiten (`component-registry.md` §ViewHeader; Konzept
 *     §1.4:206-208).
 *
 * @reuseHints
 *   - Neue Seiten auf den Kopf ziehen statt eines eigenen `<Typography variant="h5">`
 *   - Ein `titleAdornment` fuer Status statt eines eigenen Chips neben dem Titel
 *
 * @props
 *   - image: string URL | null (default null) — when truthy, renders a small
 *     rounded Avatar immediately LEFT of the title (both mobile + desktop). Null
 *     renders nothing → backward-compatible for all existing callers.
 *   - imageAlt: string (default '') — alt text for the Avatar. Pass a plain
 *     string label here; do NOT rely on `title` (callers may pass a React
 *     element, e.g. the PDP passes `title` as a <Typography> node).
 *   - title, subtitle: string OR React element. Element passes through verbatim.
 *   - backLink: { label, href } | null — der Rueckweg der SEITE. Rendert auf
 *     BEIDEN Breitenpfaden (mobil ueber dem Titel, Desktop ueber den
 *     Brotkruemen); ein `null` rendert nichts (rueckwaertsvertraeglich).
 *     🔴 Auf dem DESKTOP entfaellt er, wenn ein Brotkruem dasselbe `href`
 *     traegt (R16 W-1, 2026-10-06) — dann traegt der Kruem den Rueckweg, und
 *     die Pille waere ein zweites Bedienelement fuer dieselbe Handlung. Ohne
 *     passenden Kruem bleibt sie (B3: sonst verloere die Seite ihren einzigen
 *     Desktop-Rueckweg). Mobil unveraendert — dort gibt es keine Kruemen.
 *     Mobil traegt er eine 44-px-Trefferflaeche als Pseudo-Element (H-c,
 *     2026-10-06). Gemessen (2026-10-09) ist die Pille dort selbst 44 px hoch.
 *     🔴 Die Flaeche ist seit der Nachbesserung 1 (R16 W-3, 2026-10-06)
 *     an der Oberkante der Titelzeile verankert (gemessen 6 px unter der Pille); vorher war sie
 *     zentriert; gerechnet (mit der angenommenen 26-px-Pille) reichte sie ~3 px in die Titelzeile, auf der gemessenen 44-px-Pille waere der Ueberhang 0.
 *   - leftAdornment: ReactNode (default null) — freier Knoten LINKS vom Titel,
 *     neben `image`. Der Grund, ihn zu haben: eine Seite mit eigener Kachel
 *     (z. B. die „P"-Kachel der Bestellung) reichte sonst ihren GANZEN Titel
 *     als Element durch — und dann rendert der `h5`-Zweig nie, der Titel ist
 *     ein `<span>` ohne Ueberschrift-Semantik. Mit dem Slot bleibt `title`
 *     eine Zeichenkette und wird zur Ueberschrift wie ueberall sonst.
 *   - actionsPending: boolean (default false) — „die Seite laedt noch, es GIBT
 *     aber Aktionen". Auf Mobil ist die Aktionszeile eine eigene Zeile unter
 *     dem Titel; ohne dieses Signal waechst der Kopf, sobald die Daten
 *     eintreffen. Gesetzt (und solange nichts zu zeigen ist) reserviert der
 *     Kopf die Zeile in der Hoehe eines echten Knopfes. Desktop unberuehrt.
 *   - breadcrumbs: Array<{ label, href?, onClick? }>. Ein Eintrag MIT `href`
 *     rendert als echter `RouterLink` (Rolle `link`, fokussierbar, Enter
 *     navigiert); ein Eintrag mit NUR `onClick` als fokussierbarer
 *     `<button>`-Link; ohne beides als toter Text. 🔴 Bis zur Wiedervorlage 1
 *     (2026-10-07) war der `href`-Kruem ein MUI-`<Link>` OHNE `href`
 *     (`onClick`→`navigate`) — nicht fokussierbar, keine `link`-Rolle.
 *   - titleAdornment: ReactNode (default null) — freier Knoten INLINE direkt
 *     hinter dem Titel, auf beiden Breiten. Gedacht für STATUS (Pill,
 *     StatusPill, Chip), der bisher im `rightAdornment` sass und dort wie ein
 *     Knopf aussah (Fotobefund 2026-10-07: „Active"-Pille gefüllt direkt
 *     neben „Edit prices" outlined). Er ist ausdrücklich NICHT der
 *     Aktionsbereich — Knöpfe gehören in `actions`/`overflow` (E1).
 *   - actions: Array von Aktionsobjekten. Zusätzlich zu den MUI-Button-Props
 *     kennt der Kopf fünf eigene Schlüssel; sie werden in
 *     `renderActionButton` konsumiert und erreichen das DOM nie:
 *       · `emphasis: 'primary'` — markiert DIE primäre Aktion (E2). Bewusst
 *         nicht `primary`: das Wort heisst in RMA-Zugobjekten „empfohlen".
 *       · `menu: [{ key, label, onClick, disabled, icon, color }]` — rendert die
 *         Aktion als `SplitButton` (Hauptknopf + ▾-Menü). 🔴 **KM-1
 *         (2026-10-10): NUR, wenn der Kopf sonst KEINE ⋯-Einträge hat.** Steht
 *         die Aktion neben einem ⋯, rendert sie als einfacher Knopf und ihre
 *         Varianten stehen oben im ⋯ — ein Seitenkopf zeigt höchstens EIN Menü.
 *       · `running`, `progress` — rendern die Aktion als `ProgressButton`.
 *       · `iconOnly: true` — rendert die Aktion als `RefreshButton` (ein
 *         `IconButton`, NIE `MuiButton-contained`). Für die eine Aktion, die
 *         keine Beschriftung braucht, weil ihr Zeichen sie trägt — die
 *         Auffrischung einer Seite (T3-3, 2026-10-08). `label` ist dabei der
 *         barrierefreie Name und PFLICHT (`RefreshButton` wirft ohne ihn);
 *         `tooltip` überschreibt den Vorgabetext „Refresh". Ein `iconOnly`
 *         gewinnt gegen `menu`/`running` — es gibt keine Kombination der drei.
 *         🔴 MOBIL FOLGT DARAUS DIE TITELZEILE (T3-3/F3): sind ALLE sichtbaren
 *         Aktionen `iconOnly`, rendert der Kopf keine eigene Aktionszeile,
 *         sondern setzt sie rechts in die Titelzeile — dieselbe Ausnahme wie
 *         beim ⋯. Eine einzelne `iconOnly`-Aktion in einem Kopf MIT weiteren
 *         beschrifteten Aktionen bleibt in der Aktionszeile.
 *     Ohne `emphasis` gilt der Rückfall: die erste Aktion mit ausdrücklichem
 *     `variant: 'contained'`, sonst die erste Aktion. Sie rendert `contained`,
 *     jede andere Aktion OHNE ausdrückliches `variant` rendert `outlined`;
 *     ein ausdrückliches `variant` gewinnt immer.
 *   - overflow: Array von Menüeinträgen am ⋯-Knopf. Trägt `label`, `onClick`,
 *     `disabled`, `divider`, `color` (Färbung des Eintrags), `icon`
 *     (ListItemIcon) und `tooltip` (nativer `title`). `color` nimmt BEIDE
 *     Formen: einen nackten Palettenschlüssel (`'warning'`, wird auf den
 *     TEXT-TON `warn` der v2-Skala gezogen) oder einen fertigen Pfad
 *     (`'error.main'`, geht unverändert durch). Ein Schlüssel OHNE `v2`-Ton
 *     bekommt `.main` genau dann, wenn das Theme ihn kennt UND er dort ein
 *     `.main` trägt; `'grey'` und `'text.secondary'` tragen keins und gehen
 *     unverändert durch. Siehe `./common/menueFarbe`.
 *     🔴 B2 (2026-10-08): der Überlauf nimmt auch eine AKTION mit `menu` auf,
 *     und dann kommen ihre Untereinträge MIT — als EINGERÜCKTE Einträge im
 *     SELBEN Menü, nicht als zweites Untermenü (§5 Nr. 1 des Plans; Begründung:
 *     kein zweiter Menüzustand, tastaturfest, passt zum flachen Renderer).
 *     Ohne das verlöre eine Aktion, die mobil in den Überlauf wandert, ihre
 *     zweite Hälfte: `ScraperList` zeigt auf dem Telefon nur „Add new scraper"
 *     inline, das Paar „Preview duplicates ▾ Deduplicate" steht im ⋯ — und
 *     „Deduplicate" war dort bis zum 2026-10-08 nicht erreichbar.
 *     🔴 Welche der beiden Hälften verlorengeht, ist gemessen und nicht
 *     beliebig: `pushedToOverflow` kopiert `label: a.label` — den Namen des
 *     HAUPTKNOPFES. Bei §5 Nr. 3 ist das „Preview duplicates"; verloren geht
 *     der UNTEREINTRAG „Deduplicate". Hier stand bis zur Nachbesserung 2 das
 *     Umgekehrte (R15 W2).
 *     🔴 **KM-1 (2026-10-10): der Überlauf trägt ZWEI Formen derselben
 *     Untereinträge, und sie sind an der Lage des Knopfes zu unterscheiden.**
 *     Eine Aktion, die INLINE steht, wird zerlegt: ihre Varianten stehen oben
 *     im ⋯, **ohne Elterneintrag und ohne Einrückung**, dann ein `divider`,
 *     dann der Rest (der Knopf steht daneben, ein zweiter Weg zu derselben
 *     Handlung wäre die Doppelung, gegen die die Regel antritt). Eine Aktion,
 *     die in den Überlauf WANDERT, bringt ihre Varianten wie bisher als
 *     **eingerückte Einträge unter ihrem Namen** mit (mobil der Fall, wenn die
 *     Aktion nicht die primäre ist — `/scrapers`). Die Bedingung ist „steht der
 *     Knopf daneben?", nicht die Fensterbreite: mobil bleibt nur die primäre
 *     Aktion inline (`/products`, `/fulfillment`), und sie wird zerlegt.
 *     🔴 Die zerlegte Variante behält ihre Zielfelder (`to`/`href`/`target`/`rel`)
 *     — genau wie der gewöhnliche Überlauf, sonst wäre ein als Link gedachter
 *     Eintrag hier tot (R16-3). **Dasselbe gilt für den EINGERÜCKTEN
 *     Untereintrag** der gewanderten Aktion: er löst dieselben vier Felder mit
 *     demselben Ausdruck auf (R16-4) — sonst wäre ein `menu`-Untereintrag mit
 *     `to`/`href` genau im mobilen Fall ein toter Eintrag ohne Handler.
 *   - rightAdornment: freier Knoten im Aktionsbereich. 🔴 Seit E1 NUR noch für
 *     Formularsteuerung (`Select`, `TextField select`) und Anzeigen ohne
 *     Knopfcharakter — Knöpfe gehören in `actions`/`overflow`, Status in
 *     `titleAdornment`.
 *   - children: der Slot unter der Titelzeile — **alles, was an die Kopfkante
 *     gebunden ist** (Filter-/Chip-Streifen, Reiter- oder Navigationszeile,
 *     Statuszeile), nicht nur Filter. Der Knoten sitzt im Deckel-Kasten des
 *     Kopfes (`data-testid="viewheader-deckel"`), also in derselben Box wie der
 *     Titel; er ist darum der Weg, eine Kante an den Kopf zu binden — nicht ein
 *     zweiter `maxWidth`-Wert (R16-W2, Nachbesserung 3, 2026-10-29).
 *     🔴 Nebenwirkung, die man kennen muss: mit `children` schaltet das untere
 *     Polster von `sm: 3.5` auf `SP.sm`, und der Slot gibt `mt: 1` — der Kopf
 *     wird niedriger und alles darunter rückt nach oben.
 *   - stickyOnMobile: boolean (default false) — der GANZE Kopf klebt unter `sm`
 *     am oberen Rand (Bearbeiten-Koepfe, damit Cancel/Save erreichbar bleiben).
 *     Nebenwirkung, die E5 nutzt: mobil bleiben dann ALLE Aktionen inline.
 *   - primaryInBottomNav: boolean (default false) — 🔴 F5-4 (UI-Durchgang 2
 *     Teil 5, 2026-10-09). „Die Unterleiste traegt dieselbe Handlung." Gesetzt
 *     und nur unter `sm` faellt die PRIMAERE Aktion aus dem Kopf — und sie
 *     wandert ausdruecklich NICHT in den ⋯-Ueberlauf (sie ist im „+" der
 *     Unterleiste — und das sind ZWEI Tipps, nicht einer: das „+" oeffnet das
 *     Blatt „Create", der Eintrag sitzt darin. R15 W4 der Nachbesserung 2,
 *     2026-10-09: hier stand „einen Tipp entfernt", und das war nachpruefbar
 *     falsch). Ohne Aktionen und ohne Ueberlauf
 *     entfaellt die mobile Aktionszeile damit ganz: der Kopf schrumpft auf die
 *     Titelzeile (~42 px statt ~140).
 *
 *     🔴 WANN DAS GESETZT WERDEN DARF — die Bedingung ist eng, und sie ist
 *     gemessen, nicht geschaetzt: nur wenn der Zieldatensatz derselbe ist.
 *     `MobileBottomNav` fuehrt seine erzeugenden Eintraege in `CREATE_ITEMS`
 *     (`New Case → /rma/new`, `New Vendor → /vendors/new`); die Unterleiste ist
 *     GLOBAL, also traegt sie die Handlung auf JEDER Seite. Der Widerleger aus
 *     §5 F-1: ein Kopfknopf, der etwas anderes anlegt als das „+" (Beleg statt
 *     Seite) — dann BLEIBT er.
 *     Belegte Faelle: `/rma` (Cases → „New Case") und `/vendors` (Vendors →
 *     „Add vendor" → `/vendors/new`). Gemessen NICHT darunter, obwohl §5 F-1
 *     sie im Befund nennt: `/products` („Sync products"), `/rma/inbox` und
 *     `/po-inbox` („Check Inbox") und `/fulfillment` („Scan duplicates") —
 *     keine dieser Handlungen steht in `CREATE_ITEMS`. Dort bleibt der Knopf.
 *     Die Probe dazu: `frontend/tests/design/ui2-kopf-mobil.spec.ts` (H.2/H.2b).
 *   - maxWidth: 'layout' (Vorgabe) | ein MUI-Deckel ('xl', 'md', …) | `false` — 🔴 seit
 *     G0b.2 (2026-10-12) bedeutet `'layout'`: **der Deckel ist das Muster der
 *     Seite** aus `PageWidthContext` (`reading` 1280 / `wide` 1600); auf `full`
 *     deckelt der Kopf zunächst auf `CONTENT_WIDTH.reading` (NW4.3:649) — **nicht**
 *     deckelfrei (Konzept §1.4:200-208, NW1.3). 🔴 **Seit G0b.4 (2026-10-12)
 *     übernimmt er die von der Tabelle GEMELDETE Breite** (`tableWidth.jsx`),
 *     stufenweise höchstens eine Stufe je Frame (§4.3 Ziff. 2:617-619, NW4.6:652,
 *     PW3b) — ohne eine Meldung bleibt es beim Muster. Ein MUI-Mass schaltet die
 *     Musterbindung ab; `false` ebenso (deckelfrei, `kopfDeckel = null`), erster Aufrufer `/media`.
 */
export default function ViewHeader({
  maxWidth = 'layout',
  breadcrumbs = [],
  backLink = null,
  title,
  subtitle,
  image = null,
  imageAlt = '',
  // H-a (2026-10-06): freier Knoten LINKS vom Titel, neben `image`. Bis hierher
  // war `image` (eine URL) die einzige Kachel, die der Kopf kannte — eine Seite
  // mit eigener Kachel musste deshalb ihren GANZEN Titel als Element
  // durchreichen, und damit fiel der `h5`-Zweig aus (der Titel war ein
  // 14-px-`<span>`). Mit diesem Slot bleibt `title` eine ZEICHENKETTE und wird
  // zur Ueberschrift wie ueberall sonst. `null` rendert nichts.
  leftAdornment = null,
  // E1 (UI-Durchgang 2, 2026-10-07): Status gehoert hinter den TITEL, nicht in
  // den Aktionsbereich. Der Slot rendert inline direkt hinter dem Titel, auf
  // beiden Breiten; `null` rendert nichts (rueckwaertsvertraeglich).
  titleAdornment = null,
  actions = [],
  overflow = [],
  rightAdornment = null,
  // W-a (2026-10-06): „die Seite laedt noch, es GIBT aber Aktionen". Auf Mobil
  // steht die Aktionszeile UNTER dem Titel — ohne dieses Signal waechst der
  // Kopf in dem Moment, in dem die Daten eintreffen. Siehe `reserveActionsRow`.
  actionsPending = false,
  children,
  // When true, the entire header sticks to the top of the viewport on
  // mobile (xs only). Used by edit modes so Cancel / Save stay reachable
  // while the user scrolls a long form.
  stickyOnMobile = false,
  // F5-4 (UI-Durchgang 2 Teil 5, 2026-10-09): die Unterleiste traegt dieselbe
  // Handlung — dann faellt die Primaeraktion mobil aus dem Kopf. Begruendung und
  // die belegten Faelle stehen im `@props`-Block oben.
  primaryInBottomNav = false,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  // E-W5: der Kopf traegt den Seitenrand nur, wenn er NICHT in einem
  // `PageContainer` haengt. Ein verschachtelter Kopf wuerde ihn sonst auf den
  // Puffer des Containers addieren (Titel 24 px weiter innen als die Karten).
  const inPageContainer = React.useContext(PageContainerContext);
  // 🔴 Konzept §1.4:200-208 / NW1.3:230: der Kopf traegt DENSELBEN Deckel wie der
  // Inhalt darunter. Er liest ihn aus dem Muster der Seite (`PageWidthContext`,
  // §5.4:755-770) statt eine eigene Zahl zu fuehren — heute sind beide 1280, und
  // genau diese zufaellige Gleichheit wird hier zur Regel. Steht der Kopf
  // DANEBEN, gibt es keinen Provider: dann gilt die Vorgabe `'reading'` = 1280,
  // also genau der bisherige Wert.
  const seitenBreite = React.useContext(PageWidthContext);
  // 🔴 G0b.4 (BW3): die von der Tabelle GEMELDETE Breite (`tableWidth.jsx`).
  // Ohne eine Meldung ist sie `null` — dann bleibt der Kopf bei seinem
  // Musterdeckel, also beim heutigen Wert (additiv).
  const { tableWidth } = useTableWidth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  // Der uebernommene Deckel — `null` heisst „noch keine Messung uebernommen".
  const [uebernommenerDeckel, setUebernommenerDeckel] = React.useState(null);

  // Der Rueckweg geht zurueck in die Historie, wenn es eine gibt — sonst auf das
  // deklarierte Ziel. EINE Funktion fuer beide Breitenpfade (mobil UND Desktop):
  // bis 2026-10-06 gab es den Rueckweg nur mobil, der Desktop holte ihn aus der
  // globalen App-Leiste. B3 zieht ihn auf die Seite.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(backLink.href);
  };

  // 🔴 Nachbesserung 1 (2026-10-06), Auftrag 8 (R16 W-1): auf dem Desktop steht
  // der Rueckweg UEBER den Brotkruemen — und auf den meisten Aufrufern zeigt ein
  // Kruem auf DASSELBE Ziel (`InvoiceDetail`: Pille „Kundenrechnungen" ueber
  // Kruem „Kundenrechnungen"; ebenso Produkt, Bestellung, Fall, Lieferant,
  // Versand, beide Scraper-Unterseiten). Zwei Bedienelemente fuer dieselbe
  // Handlung sind eine Doppelung, keine Rueckversicherung.
  //
  // Die Pille entfaellt deshalb, wenn ein Kruem dasselbe `href` traegt — der
  // Kruem traegt den Rueckweg dann allein. OHNE passenden Kruem bleibt sie:
  // genau der Fall, den B3 verhindern wollte (`ProductDetail` verlor sonst
  // seinen einzigen Desktop-Rueckweg, nachdem `headerConfig.backLink` dort
  // entfernt wurde — und dessen gekuerzte Brotkrume fuehrt weiterhin auf
  // `/products`, also auf dasselbe Ziel wie der `backLink`).
  //
  // Mobil bleibt es bei der Pille: der Mobil-Zweig rendert gar keine
  // Brotkruemen, sie ist dort der einzige Rueckweg.
  const backLinkStehtImKruem = !!backLink
    && breadcrumbs.some((b) => b.href && b.href === backLink.href);

  // ── Das Aktionsmodell (E1–E5, UI-Durchgang 2, 2026-10-07) ──────────────
  //
  // 🔴 E2 — HOECHSTENS EIN gefuellter Kopfknopf, mit festem Rueckfall. Die
  // primaere Aktion ist, in dieser Reihenfolge: (1) die mit
  // `emphasis: 'primary'`; sonst (2) die erste mit ausdruecklichem
  // `variant: 'contained'`; sonst (3) die erste Aktion. Sie rendert
  // `contained`; jede andere Aktion OHNE ausdrueckliches `variant` rendert
  // `outlined`. Ein ausdrueckliches `variant` gewinnt immer.
  //
  // 🔴 Warum KEINE `console.warn` bei mehr als einer gefuellten Aktion (Grill
  // F2): die eine Flaeche, die auf Konsolenstille prueft
  // (`tests/design/bestand.spec.ts` sammelt `error` UND `warning`), steht in
  // keiner Dateiliste — eine Warnung waere dort ein Fehlalarm mit Folgen. Die
  // Invariante haelt stattdessen eine Jest-Probe (`ViewHeader.test.jsx`).
  const primaryIdx = (() => {
    if (!actions.length) return -1;
    const mitEmphasis = actions.findIndex((a) => a && a.emphasis === 'primary');
    if (mitEmphasis >= 0) return mitEmphasis;
    const mitContained = actions.findIndex((a) => a && a.variant === 'contained');
    if (mitContained >= 0) return mitContained;
    return 0;
  })();
  const primaer = primaryIdx >= 0 ? actions[primaryIdx] : null;
  const variantFuer = (a) => a.variant || (a === primaer ? 'contained' : 'outlined');

  // 🔴 E4 — EINE Reihenfolge auf beiden Breiten:
  //     [rightAdornment] [sekundaer …] [primaer] [⋯]
  // Die primaere Aktion steht rechts aussen vor dem Ueberlauf, unabhaengig von
  // ihrer Position im uebergebenen Array.
  // 🔴 E5 — mobil steht inline NUR die primaere Aktion; alles Weitere geht in
  // den Ueberlauf. Ausnahme `stickyOnMobile` (Bearbeiten-Koepfe: dort sind
  // Cancel und Save die Aktionen, fuer die der Schalter existiert) — dann
  // bleiben alle inline.
  const uebrige = actions.filter((_, i) => i !== primaryIdx);
  const maxUebrige = isMobile
    ? (stickyOnMobile ? uebrige.length : 0)
    : Math.max(0, 3 - (primaer ? 1 : 0));
  // 🔴 F5-4 (UI-Durchgang 2 Teil 5, 2026-10-09): mobil traegt die Unterleiste
  // dieselbe Handlung (`primaryInBottomNav`) — die Primaeraktion faellt aus dem
  // Kopf und geht AUSDRUECKLICH NICHT in den Ueberlauf. Sie waere dort ein
  // ZWEITER Weg zu derselben Handlung und kostete DIESELBEN zwei Tipps wie das
  // „+" der Unterleiste (beide oeffnen erst ein Blatt) — ein zweiter Weg zum
  // selben Preis, waehrend die Unterleiste den kanonischen fuehrt
  // (`CREATE_ITEMS`). R15 W4 der Nachbesserung 2 (2026-10-09): hier stand
  // „der ⋯-Eintrag kostet einen Tipp mehr als das „+"", und das war
  // nachpruefbar falsch. `uebrige` fuehrt sie ohnehin nicht (sie ist per Index
  // ausgeschlossen), der Ueberlauf bleibt also leer von ihr.
  // Nur unter `sm`: der Desktop behaelt seine Primaeraktion.
  const primaerInDerUnterleiste = isMobile && primaryInBottomNav && !!primaer;
  const sichtbarePrimaer = primaerInDerUnterleiste ? null : primaer;
  const visible = sichtbarePrimaer
    ? [...uebrige.slice(0, maxUebrige), sichtbarePrimaer]
    : uebrige.slice(0, maxUebrige);

  // 🔴 W2 — der Ueberlauf nimmt die Darstellungsfelder MIT. Unter E5 wandern
  // mobil fast alle Aktionen hierher; ohne `color` verloere das destruktive
  // „Deduplicate" (`color: 'warning'`) seine Warnfarbe, ohne `icon`/`key` der
  // Eintrag sein Zeichen und seinen Schluessel.
  const pushedToOverflow = uebrige.slice(maxUebrige).map((a) => ({
    key: a.key,
    // 🔴 Ein Menueeintrag braucht TEXT. Ein Aufrufer darf einen Knopf
    // ausblenden, weil er inline nur ein Zeichen zeigt (`VendorDetails`:
    // `label: isMobile ? undefined : 'Edit'`) — im Menue ist das Zeichen dann
    // der ganze Eintrag und der Eintrag hat nichts zu lesen. Der barrierefreie
    // Name des Knopfes ist die einzige vorhandene Beschriftung, also traegt er
    // den Eintrag.
    label: a.label || a['aria-label'],
    onClick: a.onClick,
    disabled: a.disabled || a.loading || a.running,
    color: a.color,
    icon: a.icon,
    tooltip: a.tooltip,
    // 🔴 Ein Eintrag, der als LINK gebaut war (`to` fuer einen `RouterLink`,
    // `href` fuer ein `<a>`), hat kein `onClick`. Ohne diese vier Felder waere
    // er im Menue ein Eintrag ohne Wirkung — `ProductTaxonomyDetail` baut
    // „Open product" genau so, und seit E5 landet er mobil im Ueberlauf.
    to: a.to,
    href: a.href,
    target: a.target,
    rel: a.rel,
    // 🔴 B2 (2026-10-08): die UNTEREINTRAEGE einer Aktion gehen mit. Bis
    // hierher fehlte `menu` in dieser Liste, und der Ueberlauf rendert daraus
    // einen flachen `MenuItem` — eine Aktion mit `menu`, die mobil in den
    // Ueberlauf wanderte, verlor damit ihre zweite Haelfte. Gemessen an
    // `ScraperList` nach F6: sichtbar bliebe der Hauptknopf „Preview
    // duplicates" (die Zeile darueber kopiert `a.label`), verloren ginge der
    // Untereintrag „Deduplicate" — er waere auf dem Telefon gar nicht mehr
    // erreichbar. Der Ueberlauf rendert ihn als EINGERUECKTEN Eintrag im
    // selben Menue.
    // 🔴 Hier stand bis zur Nachbesserung 2 das Umgekehrte (R15 W2: sichtbar
    // bliebe nur der Dedupe-Eintrag) — dieselbe Verwechslung wie im Grill B2,
    // dessen Rechnung den Dedupe-Knopf als Hauptknopf und die Vorschau als
    // Untereintrag ansetzt, also die umgekehrte Reihenfolge zu §5 Nr. 3.
    menu: a.menu,
  }));
  // 🔴 Reihenfolge im Menue (2026-10-07): erst was auf dem Desktop INLINE stand
  // und mobil weichen musste, dann die ausdruecklich als `overflow` gereichten
  // Eintraege. Bis hierher war es umgekehrt — richtig, solange nur wenige
  // Aufrufer ueberhaupt etwas in den Ueberlauf schoben. Mit E5 wandert mobil
  // fast alles dorthin, und dann stuenden die seltenen Eintraege („zuletzt
  // aufgeraeumt") ueber den taeglichen. Der Desktop ist unberuehrt: er zeigt
  // die ersten drei inline und schiebt nur den Rest hierher.
  const ueberlaufRest = [
    ...pushedToOverflow,
    ...(overflow.length ? overflow : []),
  ];

  // 🔴 KM-1 (2026-10-10): EIN Seitenkopf zeigt hoechstens EIN Menue. Steht eine
  // Aktion INLINE und traegt ein `menu`, waehrend der ⋯ ohnehin Eintraege hat,
  // dann stehen dort ZWEI Menues nebeneinander — genau das Pattern der
  // Owner-Meldung („Button mit Kontext-Menue und dann noch ein dreipunkt menue
  // neben dran"). Der Kopf zerlegt die Aktion dann: einfacher Knopf (der
  // Fortschritt bleibt, s. `renderActionButton`), und ihre Varianten wandern
  // nach OBEN in den ⋯.
  //
  // 🔴 OHNE Elterneintrag und OHNE Einrueckung. Die Einrueckung ist im Code
  // ausschliesslich visuell (`pl: SP.lg`, weiter unten) — ohne den Elternteil
  // waere sie eine Auszeichnung ohne Ausgezeichnetes. Und ein Elterneintrag
  // waere ein ZWEITER Weg zu derselben Handlung: der Knopf steht daneben.
  // 🔴 Genau daran haengt auch der Unterschied zum Bestand: mobil wandert
  // dieselbe Aktion in den ⋯, WEIL ihr Knopf dort fehlt — dort bleibt es bei
  // Elterneintrag + eingerueckten Untereintraegen (`pushedToOverflow`, oben).
  // Die Bedingung ist „steht der Knopf daneben?", nicht die Fensterbreite. Auf
  // dem Desktop trifft sie immer; mobil trifft sie die PRIMAERE Aktion (die
  // einzige, die dort inline bleibt — `/products`, `/fulfillment`) und jede
  // Aktion unter `stickyOnMobile`. Eine nicht-primaere Aktion wandert mobil in
  // den ⋯ (`/scrapers`) und behaelt dort Elterneintrag + Einrueckung.
  //
  // 🔴 Die Sperre des Hauptknopfes gilt fuer die Varianten MIT — dieselbe Summe
  // wie mobil (`u.disabled || m.disabled`) und STRENGER als der `SplitButton`,
  // der nur seinen Pfeil sperrt. Ohne sie waere die zerstoerende Haelfte
  // waehrend eines laufenden Vorgangs klickbar.
  const inlineMitMenu = visible.filter((a) => Array.isArray(a.menu) && a.menu.length > 0);
  const menueZerlegt = inlineMitMenu.length > 0 && ueberlaufRest.length > 0;
  const overflowable = menueZerlegt
    ? [
      ...inlineMitMenu.flatMap((a) => a.menu.map((u) => ({
        key: `${a.key ?? a.label}-${u.key ?? u.label}`,
        label: u.label,
        onClick: u.onClick,
        disabled: u.disabled || a.disabled || a.loading || a.running,
        color: u.color,
        icon: u.icon,
        tooltip: u.tooltip,
        // 🔴 R16-3 (2026-10-10): die Zielfelder gehen MIT — genau wie im
        // gewoehnlichen Ueberlauf (Renderer unten). Ohne sie waere ein als
        // LINK gedachter Varianteneintrag hier ein toter Eintrag: er haette
        // keinen Handler, und `MenuItem` rendert ihn ohne `href`.
        to: u.to,
        href: u.href,
        target: u.target,
        rel: u.rel,
      }))),
      // Der Trenner ist die EINZIGE Trennung zwischen „Varianten der
      // Hauptaktion" und „Rest" — der ⋯ kennt keinen Gruppentitel, nur
      // `divider` (Renderer unten).
      { divider: true },
      ...ueberlaufRest,
    ]
    : ueberlaufRest;

  const useLayoutBounds = maxWidth === 'layout';
  const containerMax = useLayoutBounds ? false : maxWidth;
  // Der Deckel des Kopfes = das Muster der Seite (NW1.3:230). 🔴 Auf einer
  // `full`-Seite deckelt der Kopf auf `CONTENT_WIDTH.reading` — NICHT deckelfrei:
  // die Tabellenbreite ist beim ersten Anstrich unbekannt, und ein 3160 px
  // breiter Kopf mit Titel links und Aktion rechts ist keine Kopfzeile mehr
  // (Konzept §4.2:594, §4.3:614-616, NW4.3:649). Ein ausdrueckliches
  // `maxWidth`-Prop (auch `false`) schaltet die Musterbindung ab (Sonderfall, unveraendert).
  //
  // 🔴 G0b.5 (2026-10-13): der tote Rueckfall auf eine NICHT existierende
  // Theme-Variable (`theme.vars.layout`, ohne `cssVariables: true`) ist
  // ENTFALLEN (NW5.4:806) — der Ausdruck ergab **immer** den reading-Wert
  // (`inhaltsbreite.md` §5.2:687-695). Der Deckel kommt allein aus dem Token;
  // kein Zahlen-Rueckfall daneben.
  const musterDeckel =
    seitenBreite === 'full' ? CONTENT_WIDTH.reading : CONTENT_WIDTH[seitenBreite];

  // 🔴 G0b.4 (BW3) — der Kopf UEBERNIMMT die gemeldete Tabellenbreite, aber
  // stufenweise (NW4.6:652, §4.3 Ziff. 2:617-619, Pruefung PW3b:1005):
  //   1. ERSTER ANSTRICH = der Musterdeckel (§4.3:614-616) — nichts springt,
  //      weil das der heutige Zustand ist (`uebernommenerDeckel === null`).
  //   2. Dann je Frame HOECHSTENS EINE STUFE (`naechsteStufe`) bis zum Ziel.
  //   3. Keine Meldung (`tableWidth === null`) ⇒ zurueck auf den Musterdeckel.
  // 🔴 Die Drosselung liegt hier, nicht im Beobachter: `useContainerWidth`
  // liefert die ROHE Breite in EINEM Bild (R16-W4) — das Stufengaengige ist
  // Sache des Konsumenten, und der Konsument ist der Kopf.
  React.useEffect(() => {
    if (!useLayoutBounds) return undefined;
    if (tableWidth == null) {
      setUebernommenerDeckel(null);
      return undefined;
    }
    if (uebernommenerDeckel === tableWidth) return undefined;
    const rahmen = requestAnimationFrame(() => {
      setUebernommenerDeckel((vorher) => naechsteStufe(vorher ?? musterDeckel, tableWidth));
    });
    return () => cancelAnimationFrame(rahmen);
  }, [tableWidth, uebernommenerDeckel, musterDeckel, useLayoutBounds]);

  const kopfDeckel = useLayoutBounds ? uebernommenerDeckel ?? musterDeckel : null;

  // Optional product/entity thumbnail rendered to the LEFT of the title.
  // Null/absent → nothing rendered (backward-compatible). flexShrink:0 so it
  // never gets squeezed by a long title in the row.
  const headerAvatar = image ? (
    <Avatar
      variant="rounded"
      src={image}
      alt={imageAlt}
      sx={{ width: 44, height: 44, flexShrink: 0, bgcolor: 'action.hover' }}
    >
      <Inventory2OutlinedIcon fontSize="small" color="disabled" />
    </Avatar>
  ) : null;

  const renderActionButton = (a) => {
    const {
      key,
      label,
      onClick,
      disabled,
      color = 'primary',
      icon,
      endIcon,
      loading,
      component,
      href,
      target,
      rel,
      startIcon,
      // 🔴 W3: die EIGENEN Schluessel des Kopfes werden HIER konsumiert.
      // Stuenden sie in `...rest`, reichte MUI sie an das DOM durch und React
      // warnte „React does not recognize the `running` prop". `ignoreRestSiblings`
      // der CRA-Regel laesst das Destrukturieren ohne Verwendung zu.
      emphasis,
      menu,
      running,
      progress,
      // 🔴 T3-3 (2026-10-08): fuenfter eigener Schluessel, aus demselben Grund
      // wie die vier darueber hier konsumiert — stuende er in `...rest`,
      // reichte MUI ihn an das DOM durch.
      iconOnly,
      ...rest
    } = a;
    const variant = variantFuer(a);
    const hatMenu = Array.isArray(menu) && menu.length > 0;

    // 🔴 T3-3 (2026-10-08): eine Aktion, deren Zeichen ihre Beschriftung IST.
    // Der Kopf rendert sie als `RefreshButton` — ein `IconButton`, NIE
    // `MuiButton-contained`. Genau darum geht es: E2 faellt auf `contained`
    // zurueck, wenn eine Aktion keine ausdrueckliche Variante traegt, und eine
    // Auffrischung ist NIE primaer (`RefreshButton.jsx` @dontUseFor). Ohne
    // diesen Zweig muesste der Aufrufer einen `variant`-Wert erfinden, den es
    // fuer einen Symbolknopf nicht gibt.
    if (iconOnly) {
      return (
        <RefreshButton
          key={key}
          // `label` ist hier der barrierefreie Name und PFLICHT —
          // `RefreshButton` wirft ohne ihn. `iconOnly` ohne `label` ist ein
          // Fehler der Aufrufstelle, kein Zustand, den der Kopf rendern kann.
          ariaLabel={label}
          tooltip={a.tooltip}
          loading={loading}
          firstLoad={a.firstLoad}
          onClick={onClick}
          disabled={disabled}
        />
      );
    }
    // `running` wird nur als Faehigkeit gewertet, wenn der Aufrufer den
    // Schluessel ueberhaupt gesetzt hat — `running: false` ist eine Aussage
    // („dieser Knopf kann Fortschritt zeigen"), `undefined` ist keine.
    const istProgress = Object.prototype.hasOwnProperty.call(a, 'running');

    // 🔴 KM-1 (2026-10-10): ein `menu` rendert NUR dann einen `SplitButton`,
    // wenn der Kopf sonst keinen ⋯ traegt. Steht die Aktion neben einem ⋯, ist
    // sie ein einfacher Knopf — `ProgressButton` mit `running`, sonst `Button`
    // (beide Zweige darunter) — und ihre Varianten stehen oben im ⋯
    // (`overflowable`, oben). Der `ProgressButton` setzt seine Live-Region
    // SELBST; der `SplitButton` tat das fuer seine linke Haelfte mit
    // `statusRegion={false}` plus eigener `ProgressStatus`. Genau EINE Region
    // bleibt es in beiden Faellen.
    if (hatMenu && !menueZerlegt) {
      return (
        <SplitButton
          key={key}
          label={label}
          onClick={onClick}
          menu={menu}
          variant={variant}
          color={color}
          startIcon={startIcon || icon || null}
          disabled={disabled || loading}
          running={running}
          progress={progress}
        />
      );
    }
    if (istProgress) {
      return (
        <ProgressButton
          key={key}
          label={loading ? '…' : label}
          onClick={onClick}
          variant={variant}
          color={color}
          startIcon={startIcon || icon || null}
          disabled={disabled || loading}
          running={running}
          progress={progress}
        />
      );
    }
    return (
      <Button
        key={key}
        size="small"
        variant={variant}
        color={color}
        onClick={href ? undefined : onClick}
        disabled={disabled || loading}
        startIcon={startIcon || icon || null}
        endIcon={endIcon}
        component={component}
        href={href}
        target={target}
        rel={rel}
        {...rest}
      >
        {loading ? '…' : label}
      </Button>
    );
  };

  const overflowMenu = !!overflowable.length && (
    <>
      {/* The icon is aria-hidden and there is no text child, so without an explicit
          name this button has NO accessible name at all: a screen reader announces
          "button" and nothing else, and getByRole('button', { name }) cannot find
          it — which is how a Playwright spec for a dialog behind this menu came to
          be written against a selector that could never match. */}
      <IconButton
        size="small"
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {overflowable.map((m, idx) => {
          if (m.divider) return <Divider key={idx} />;
          // Ein Eintrag, der als LINK gedacht war, bleibt im Menue ein Link —
          // sonst hat er keinen Handler und ist tot.
          const ziel = m.to
            ? { component: RouterLink, to: m.to }
            : m.href
              ? { component: 'a', href: m.href, target: m.target, rel: m.rel }
              : {};
          return (
            <React.Fragment key={m.key ?? idx}>
              <MenuItem
                {...ziel}
                onClick={() => { setMenuAnchor(null); m.onClick && m.onClick(); }}
                disabled={m.disabled}
                title={m.tooltip || undefined}
                sx={m.color ? { color: menueFarbe(m.color, theme) } : undefined}
              >
                {/* 🔴 W2: das Zeichen des Knopfes geht in den Ueberlauf mit —
                    sonst verliert der Eintrag im Menue genau das, was ihn im
                    Kopf erkennbar machte. */}
                {m.icon ? <ListItemIcon>{m.icon}</ListItemIcon> : null}
                {m.label}
              </MenuItem>
              {/* 🔴 B2 (2026-10-08): die Untereintraege einer Aktion mit `menu`.
                  Sie stehen als EINGERUECKTE Eintraege im SELBEN Menue — kein
                  zweites Untermenue (§5 Nr. 1). Die Einrueckung ist die
                  EINZIGE Unterscheidung zwischen Haupt- und Untereintrag; ohne
                  sie laese die Liste wie vier gleichrangige Aktionen.
                  🔴 R16 W2 (Nachbesserung 2): die Sperre des HAUPTEINTRAGS gilt
                  fuer die Untereintraege MIT. `m.disabled` ist bereits
                  `a.disabled || a.loading || a.running` (`pushedToOverflow`) —
                  ohne diesen Zusatz blieb die zweite Haelfte waehrend eines
                  laufenden Vorgangs klickbar, waehrend `SplitButton` dieselbe
                  Knopfpaarung mit `gesperrt = disabled || running` auf BEIDEN
                  Haelften sperrt (`SplitButton.jsx`). Dieselbe Paarung darf
                  sich in ihren zwei Formen nicht verschieden verhalten. */}
              {Array.isArray(m.menu) && m.menu.map((u, uIdx) => {
                // 🔴 R16-4 (2026-10-10): dieselben Zielfelder wie der flache
                // Zweig oben (`ziel`) — ein als LINK gedachter Untereintrag
                // waere im mobilen (gewanderten) Fall sonst ein toter Eintrag.
                const ziel = u.to
                  ? { component: RouterLink, to: u.to }
                  : u.href
                    ? { component: 'a', href: u.href, target: u.target, rel: u.rel }
                    : {};
                return (
                  <MenuItem
                    key={`${m.key ?? idx}-${u.key ?? uIdx}`}
                    {...ziel}
                    onClick={() => { setMenuAnchor(null); u.onClick && u.onClick(); }}
                    disabled={u.disabled || m.disabled}
                    sx={{ pl: SP.lg, ...(u.color ? { color: menueFarbe(u.color, theme) } : {}) }}
                  >
                    {u.icon ? <ListItemIcon>{u.icon}</ListItemIcon> : null}
                    {u.label}
                  </MenuItem>
                );
              })}
            </React.Fragment>
          );
        })}
      </Menu>
    </>
  );

  // 🔴 F12/T3-15 (2026-10-08): traegt der ⋯ den Aktionsbereich als EINZIGES
  // (keine sichtbare Aktion, kein `rightAdornment`), dann wandert er in die
  // TITELZEILE — die eigene Aktionszeile entfaellt. Sie war auf der Fallansicht
  // mobil eine Zeile mit einem einsamen ⋮ darin. Vorher haengte er links in
  // einer vollen Zeile, also an der falschen Stelle fuer einen Menue-Ausloeser.
  const onlyOverflow = visible.length === 0 && !rightAdornment && !!overflowMenu;

  // 🔴 T3-3/F3 (Nachbesserung 2, 2026-10-08): dasselbe fuer den Auffrisch-Knopf.
  // Traegt der Kopf sichtbar NUR `iconOnly`-Aktionen, stand mobil eine eigene
  // Zeile mit einem einsamen Symbol darin — die Fotobefunde `home.m.png` und
  // `invoicing.m.png` zeigen es links unter dem Untertitel, waehrend der
  // Desktop richtig war. T3-3 bestand mobil also fort, obwohl die erste
  // Nachbesserung ihn fuer erledigt hielt. Sie wandern jetzt wie der ⋯ nach
  // rechts in die Titelzeile.
  //
  // 🔴 `visible.length > 0` ist die Bedingung, die den HOEHENSPRUNG verhindert
  // (R15 W4 der Runde 1). Ein LEERER Aktionsbereich ist nicht „alles Symbole",
  // sondern „noch nichts da" — und genau fuer ihn existiert `reserveActionsRow`
  // darunter. Ohne die Laengenpruefung waere `every` auf dem leeren Array WAHR,
  // jeder ladende Kopf (`ProductDetail`, `PODetail`, `VendorDetails`) verloere
  // seine reservierte Zeile und der Kopf waechse, sobald die Daten eintreffen —
  // genau der Sprung, gegen den `actionsPending` gebaut wurde.
  const nurSymbolAktionen = visible.length > 0 && visible.every((a) => a.iconOnly);

  // 🔴 W-a (2026-10-06): drei Seiten (`ProductDetail`, `PODetail`,
  // `ScraperList`) tragen im Lade-Zweig noch keine Aktionen — die brauchen ihre
  // Daten. Auf Mobil ist die Aktionszeile eine EIGENE Zeile unter dem Titel;
  // fehlt sie, waechst der Kopf genau in dem Moment, in dem die Daten
  // eintreffen. `actionsPending` reserviert die Zeile hoehengleich. NUR mobil:
  // auf dem Desktop sitzen die Aktionen rechts in derselben Zeile, ihre
  // Abwesenheit aendert die Kopfhoehe nicht.
  //
  // 🔴 R15 W4 (Runde 1): `onlyOverflow` liest `actionsPending` nicht. Das ist
  // jetzt gegengeprueft und bleibt so — die Bedingungen schliessen einander
  // aus, statt sich zu widersprechen: `onlyOverflow` verlangt `overflowMenu`,
  // `reserveActionsRow` verlangt `!overflowMenu`; `nurSymbolAktionen` verlangt
  // `visible.length > 0`, `reserveActionsRow` verlangt `visible.length === 0`.
  // Gemessen an allen Aufrufern: die drei `actionsPending`-Koepfe
  // (`ProductDetail.jsx:1923`, `PODetail.jsx:3327`, `VendorDetails.jsx:2391`)
  // tragen keine `iconOnly`-Aktion; der eine
  // `iconOnly`-Aufrufer (`HomeDashboard.jsx:728`)
  // traegt kein `actionsPending`. 🔴 Bis G7a (2026-10-20) war
  // `InvoiceList.jsx` der zweite; dort steht der Refresh seither im Rumpf
  // (`RefreshButton`), der Kopf traegt keine Aktion mehr. Die verbleibende
  // Falle ist damit benannt statt still: wer einem ladenden Kopf spaeter eine
  // `iconOnly`-Aktion gibt UND `actionsPending` setzt, verliert die
  // reservierte Zeile.
  const reserveActionsRow = isMobile && actionsPending
    && visible.length === 0 && !rightAdornment && !overflowMenu;

  // 🔴 Nachbesserung 1 (2026-10-06), Auftrag 5 (d): „hat dieser Kopf eine
  // Aktionszeile?" ist EINE Frage, und sie wurde an ZWEI Stellen verschieden
  // beantwortet. Die Zeile selbst prueft `visible || rightAdornment ||
  // overflowMenu || reserveActionsRow`; der Abstand der Titelzeile darunter
  // prueft nur `visible || overflowable`. Im Ladezweig (`reserveActionsRow`,
  // keine sichtbare Aktion) rendert die Zeile also, der Titel bekommt aber
  // KEIN `mb` — der Kopf ist dort 10 px kuerzer als geladen.
  //
  // Gemessen an `/purchase_order/:id` bei 390 px: Ladeinhalt y = 234,
  // geladen y = 212. Davon sind 32 px der `mt: 4` des Skeletts (eigener
  // Befund, `PODetailSkeleton`) und 10 px genau dieses fehlende `mb`
  // (`mb: 1.25`). Mit EINER Groesse fuer beide Stellen faellt der zweite
  // Anteil weg.
  // 🔴 F12 (2026-10-08) und T3-3 (Nachbesserung 2): die zwei Ausnahmen oben
  // ziehen den Aktionsbereich in die TITELZEILE; dann entfaellt die eigene
  // Zeile. `titelzeilenAktionen` und `hasActionsRow` werden AUSSCHLIESSLICH im
  // Mobil-Zweig gelesen — der Desktop ist unberuehrt.
  //
  // 🔴 R15 W3 der Wiedervorlage 1 (2026-10-08, latent, bewusst NICHT geaendert):
  // diese Zeile zieht bei ausschliesslich `iconOnly`-Aktionen MIT nicht-leerem
  // `overflow` BEIDES in die Titelzeile — `visible.map(renderActionButton)` UND
  // den `overflowMenu` (`:944-949`). Diese Kombination faehrt heute KEIN
  // Aufrufer (nur `HomeDashboard.jsx:728` nutzt `iconOnly`, ohne `overflow`;
  // `InvoiceList.jsx` ist seit G7a kein `iconOnly`-Aufrufer mehr) und KEINE
  // Probe pinnt sie. Wer ihr als erster Aufrufer begegnet, bekommt Symbol UND
  // ⋯ nebeneinander in der Titelzeile — ob das gewollt ist, entscheidet
  // niemand hier.
  const titelzeilenAktionen = !rightAdornment && (onlyOverflow || nurSymbolAktionen);
  const hasActionsRow = reserveActionsRow
    || (!titelzeilenAktionen && (visible.length > 0 || !!rightAdornment || !!overflowMenu));

  // 🔴 E1: `titleAdornment` sitzt INLINE hinter dem Titel — auf beiden
  // Breiten dieselbe Stelle. Ist er leer, gibt `mitTitelAdornment` den Knoten
  // unveraendert zurueck (kein zusaetzlicher `Stack`, keine geaenderte Breite).
  // 🔴 Eingegrenzt 2026-10-08 (R15 W5a): das gilt fuer DIESEN Helfer, nicht fuer
  // das Desktop-Markup als Ganzes — der Desktop-Zweig traegt seit F6 (2026-10-07)
  // immer einen `Box`-Wrapper um Titel UND Untertitel, auch ohne Adornment.
  const titelKnotenMobil = React.isValidElement(title) ? title : (
    // Karte 3 (Welle 4, 2026-10-07): der STRING-Zweig IST die
    // Seitenueberschrift — `component="h1"` setzt den Tag, `variant="h6"`
    // bleibt das Aussehen. Der Element-Zweig darueber bleibt unberuehrt.
    <Typography component="h1" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
      {title}
    </Typography>
  );
  const titelKnotenDesktop = React.isValidElement(title) ? title : (
    // Karte 3 (Welle 4, 2026-10-07): wie im Mobil-Zweig — `component="h1"`
    // setzt nur den Tag, `variant="h5"` bleibt das Aussehen.
    <Typography component="h1" variant="h5" noWrap sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
  );
  // 🔴 R16 B-1 (Wiedervorlage 2, 2026-10-07): zwei Chips brachen bei 1440 px
  // GEMEINSAM unter den Titel (`ProductDetail`: „Active" + „Discontinued — no
  // reorders", gemessen, +24 px Kopfhoehe). Ursache war `flexWrap: 'wrap'`:
  // der Zeilenumbruch wird aus den BASIS-Breiten gerechnet, also schob ein
  // langer Titel das Adornment auf die naechste Zeile, statt selbst zu
  // schrumpfen. Gewaehlt ist Weg (b) — `nowrap` ab `md`: Titel und Adornment
  // bleiben strukturell in EINER Zeile, der Titel gibt nach.
  //   · Titel: `min-width: 0` (`& > :not(:last-child)`) — damit darf er
  //     schrumpfen. Der Zeichenketten-Titel traegt `noWrap` und ellipsiert; der
  //     Produkttitel (`wordBreak: break-word`) bricht INNEN um. Kein
  //     waagerechtes Ueberlaufen, auf keiner Breite.
  //     🔴 NICHT `:first-child`: `@emotion/cache` traegt in der Entwicklung
  //     einen Alarm, der auf `(:first|:nth|:nth-last)-child` `console.error`
  //     ruft („potentially unsafe when doing server-side rendering"). Er feuerte
  //     auf JEDER Seite mit `titleAdornment` — `AdminLayout`,
  //     `ScraperConfigForm`, `ProductDetail` — und war der einzige Grund, aus
  //     dem `tests/admin-spas/lsm-inventory-sync.spec.ts` auf diesem Zweig rot
  //     wurde. `:not(:last-child)` trifft bei zwei Kindern dasselbe Element.
  //     `:first-of-type` waere der vom Alarm vorgeschlagene Ersatz, hier aber
  //     falsch: Titel und Adornment koennen denselben Elementtyp tragen.
  //   · Adornment: `flex-shrink: 0` (`& > :last-child`) — die Chips behalten
  //     ihre Breite und werden NICHT abgeschnitten.
  // Unter `sm` bleibt `wrap`: dort ist der Kopf ohnehin senkrecht, und ein
  // Umbruch ist die ruhigere Form als eine ellipsierte Seitenueberschrift.
  // 🔴 Die zwei Regeln greifen ueber Kind-Selektoren, NICHT ueber zusaetzliche
  // `Box`-Wrapper — das DOM der Titelzeile bleibt damit unveraendert (die
  // Proben (10)/(10m) vergleichen `titel.parentElement` mit `status.parentElement`).
  const mitTitelAdornment = (knoten) => (
    titleAdornment ? (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          minWidth: 0,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          // 🔴 Die zwei Regeln sind fuer GENAU ZWEI Kinder gebaut — Titel + EIN
          // Adornment-Knoten. Dann ist `:not(:last-child)` der Titel und
          // `:last-child` das Adornment: dieselben zwei Elemente, die vorher
          // `:first-child` und `:last-child` trafen.
          //   · MEHR als zwei Kinder sind nicht abgedeckt: jetzt duerfen alle
          //     Kinder ausser dem letzten schrumpfen (vorher nur das erste),
          //     das letzte behaelt seine Breite. Mehrere Adornments deshalb in
          //     einen eigenen `Stack` packen, wie
          //     `ProductDetail.renderHeaderAdornments`.
          //   · Genau EIN Kind — ein Adornment, das leer rendert — traegt nur
          //     `flex-shrink: 0`. Das ist ohne Unterschied: ein `flex-shrink: 0`
          //     macht ein `min-width: 0` daneben ohnehin unwirksam.
          '& > :not(:last-child)': { minWidth: 0 },
          '& > :last-child': { flexShrink: 0 },
        }}
      >
        {knoten}
        {titleAdornment}
      </Stack>
    ) : knoten
  );

  return (
    <Box
      // 🔴 F5-4 (UI-Durchgang 2 Teil 5, 2026-10-09): die Messflaeche des
      //    Seitenkopfs. Ohne eine benannte Flaeche muesste die Probe vom `h1`
      //    aus Vorfahren hochsteigen und traefe je nach Seite einen anderen
      //    Kasten — die Hausform verlangt BENANNTE Messflaechen
      //    (`tests/design/ui2-kopfzeile.spec.ts` §„Die Messflächen sind
      //    benannt"). Gemessen wird `H.2` in `tests/design/ui2-kopf-mobil.spec.ts`.
      data-testid="viewheader"
      sx={{
      // 🔴 F5-4 (UI-Durchgang 2 Teil 5, 2026-10-09): unter `sm` 8 px statt 16 —
      //    der Kopf trug mobil 16 + Titelzeile + 16 = ~58 px, mit der entfallenden
      //    Aktionszeile misst er ~42 px (Desktop unberuehrt); Beleg H.2/H.2b.
      //    🔴 R16 W5: `SP.tight` (1 = 8 px) und `SP.sm` (1.5 = 12 px) sind
      //    wertgleich; `sm: 2.5`/`3.5` haben kein Token (`SP`: 0.5/1/1.5/2/3/4/6)
      //    und bleiben darum rohe Zahlen — benannte Ausnahme, keine Wertänderung.
      pt: { xs: SP.tight, sm: 2.5 },
      pb: children ? SP.sm : { xs: SP.tight, sm: 3.5 },
      bgcolor: stickyOnMobile ? { xs: 'background.default', sm: 'transparent' } : 'transparent',
      // Mobile-only sticky: keeps Edit/Cancel/Save anchored at the top of
      // the viewport while the form scrolls. Desktop stays static.
      ...(stickyOnMobile ? {
        position: { xs: 'sticky', sm: 'static' },
        top: { xs: 0 },
        zIndex: { xs: (theme) => theme.zIndex.appBar - 1 },
        boxShadow: { xs: '0 1px 0 rgba(15,23,42,0.08)', sm: 'none' },
      } : {}),
    }}>
      <Container
        maxWidth={containerMax}
        disableGutters
        // 🔴 E-W5: KEIN eigener Puffer neben einem `PageContainer` (der traegt
        // ihn), sonst addieren sich beide. Der Kontext oben unterscheidet die
        // zwei Faelle — ein blosses Weglassen genuegt nicht, der
        // `MuiContainer`-Theme-Override setzt `paddingLeft/Right` auf JEDEM
        // Container, `sx` ist die einzige Ebene darueber.
        sx={{ px: inPageContainer ? 0 : PAGE_GUTTER }}
      >
        <Box
          // 🔴 Benannte Messflaeche (R16-W2, Nachbesserung 3, 2026-10-29): der
          // Kasten, der den Deckel traegt — Titelzeile UND `children`-Slot
          // liegen darin. Eine Probe kann damit „dieselbe Box" pruefen, statt
          // einen zweiten `maxWidth`-Wert zu vergleichen (jsdom hat kein
          // Layout; ein Stilwert belegt keine Lage).
          data-testid="viewheader-deckel"
          sx={{
            // 🔴 G0b.5 (2026-10-13), Bedingung 1 „Kopf bündig links" (R16 W2 /
            // R15 W4): sobald eine Tabelle eine KLEINERE Breite meldet als der
            // Behälter, deckelt der Kopf auf die Tabellenbreite — und `mx: 'auto'`
            // zentrierte ihn dann, während die Tabelle links bleibt: PW3
            // (`|x(Kopf) − x(Tabelle)| ≤ 1 px`, NW1.3) riss, und mit dem
            // Stufengang wanderte der linke Rand mit (PW3b verspricht „kein
            // Ruckeln"). Die Regel lautet deshalb `mx: 0`, **sobald
            // `tableWidth != null`** — BEDINGT, damit der heutige Stand ohne
            // Meldung byte-gleich bleibt (`uebernommenerDeckel === null` ⇒
            // zentriert wie bisher). Quelle: Plan §3.3 „Verbindliche Bedingungen
            // für den ersten Verbraucher", Konzept §4.1:573 / §4.2:599.
            //
            // 🔴 **Die zwei Groessen sind VERSCHIEDEN, und das ist Absicht**
            // (R16-W4 / R15-Beobachtung 6): `tableWidth` ist die ROHE Meldung und
            // steht im selben Render, in dem sie eintrifft; `uebernommenerDeckel`
            // zieht gestuft nach (der `requestAnimationFrame` in `:640-651` laeuft
            // erst im naechsten Bild). Der linke Rand springt deshalb EINMAL von
            // zentriert auf linksbuendig, waehrend die BREITE danach noch
            // stufenweise nachzieht. Waere `mx` an `uebernommenerDeckel` gebunden,
            // bliebe der Kopf einen Frame lang zentriert neben einer schon
            // linksbuendigen Tabelle — PW3 risse. ⚠️ **Keine Probe misst den
            // linken Rand**; PW3b misst nur `maxWidth`.
            mx: tableWidth != null ? 0 : 'auto',
            width: '100%',
            ...(kopfDeckel === null ? {} : { maxWidth: kopfDeckel }),
          }}
        >
          {isMobile ? (
            /* ── Mobile: title full-width, actions below ── */
            <>
              {/* Back link — goes back in history when possible, else to backLink.href.
                  Prevents extra history entries when the user arrived via in-app navigation. */}
              {backLink && (
                <Button
                  startIcon={<ArrowBackIcon sx={{ fontSize: '1rem !important' }} />}
                  size="small"
                  variant="text"
                  onClick={goBack}
                  sx={{
                    px: 0, mb: 0.75, color: 'text.secondary', fontSize: '0.8rem', py: 0.25,
                    // H-c (2026-10-06): `MuiButton.root` traegt auf `down('sm')`
                    // `minHeight: 44` (`theme.js`, iOS-HIG). Gerechnet hiess es,
                    // `minHeight: 'unset'` nehme das weg (~26 px) — 🔴 GEMESSEN
                    // (2026-10-09) gewinnt der Boden, `unset` ist wirkungslos, die Pille ist 44 px.
                    // Die Flaeche (Pseudo-Element, 44 px) ist damit fast redundant: nur 6 px darunter,
                    // ohne jeden Einfluss auf das Layout (dieselbe Hausform wie
                    // `TriageLine.jsx` / `RecommendationCard.jsx`).
                    // Waagerecht bleibt es bei der Pillenbreite — sie liegt
                    // ueber 44 px und waechst mit der Beschriftung.
                    //
                    // 🔴 Nachbesserung 1 (2026-10-06), Auftrag 9 (R16 W-3): die
                    // Flaeche war ZENTRIERT auf der Pille (`top: '50%'`,
                    // `translate(-50%, -50%)`). Bei einer GERECHNETEN Pille von ~26 px
                    // (mit 1rem = 16 px gerechnet; das Haus setzt 14 px, gemessen ist
                    // die Zeile 19,6 px hoch und die Pille 44) ragte sie (44 − 26)/2 = 9 px
                    // ueber UND unter die Pille. Unter der Pille stehen aber nur
                    // `mb: 0.75` = 6 px bis zur Titelzeile — die Flaeche reichte
                    // also 3 px IN die Titelzeile hinein, und ein Tipp auf ihren
                    // oberen Rand loeste „zurueck" aus.
                    //
                    // Seitdem liegt ihre Unterkante auf der Oberkante der Titelzeile
                    // (`bottom: '-6px'` = `mb: 0.75`), die 44 px gehen von dort nach
                    // oben — bei der 44-px-Pille also 6 px TIEFER als die Pille. 🔴 Gemessen 2026-10-09
                    // bei 390 px (M5, `pt` seit F5-4 `SP.tight` = 8 px): die Pille
                    // ist dort selbst 44 px hoch (61–105), die Flaeche 67–111 —
                    // unter Kopfrand (53) und App-Leiste (bis 45); am oberen Rand
                    // trifft `elementFromPoint` den Rueckweg, 2 px unter ihrer Unterkante den Titel.
                    // 🔴 `'-6px'` als ZEICHENKETTE — der WERT ist richtig, die
                    // hier bis zur Nachbesserung 7 (R15 W1, Runde 4) stehende
                    // BEGRUENDUNG war es nicht. Dort stand, `sx` multipliziere
                    // nackte Zahlen bei `top`/`right`/`bottom`/`left` mit dem
                    // Spacing-Faktor, `bottom: -6` waere also −48 px.
                    // **Gemessen ist das falsch:** in `@mui/system` 7.3.5 traegt
                    // die Positionengruppe KEIN `themeKey: 'spacing'`
                    // (`styleFunctionSx/defaultSxConfig.js:247-250` — `top: {}`,
                    // `right: {}`, `bottom: {}`, `left: {}`; nur `m*`/`p*`/`gap`
                    // tun das). `bottom: -6` renderte `-6px`, genau wie die
                    // Zeichenkette. Die Zeichenkette bleibt trotzdem: sie sagt
                    // die Einheit selbst und haengt damit an keiner Annahme
                    // ueber den Faktor. Wer sie in eine Zahl zurueckbaut, aendert
                    // heute nichts — und morgen vielleicht doch.
                    minHeight: 'unset',
                    position: 'relative',
                    '&::after': {
                      content: '""', position: 'absolute',
                      left: '50%', transform: 'translateX(-50%)',
                      width: '100%', height: 44,
                      bottom: '-6px',
                    },
                  }}
                >
                  {backLink.label}
                </Button>
              )}
              {/* Title row — avatar (optional) sits LEFT of the title column. */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: hasActionsRow ? 1.25 : 0 }}
              >
                {headerAvatar}
                {leftAdornment}
                {/* 🔴 F12 + T3-3 (2026-10-08): `flex: 1` NUR im
                    `titelzeilenAktionen`-Fall. Dort ist es das Mittel, das den
                    Aktionsbereich an den rechten Rand schiebt (W6 des Grills:
                    NICHT `justifyContent: 'space-between'` am `Stack` — das
                    zoegte ALLE Kinder auseinander, Avatar links, Titel mittig,
                    ⋯ rechts).
                    🔴 Warum nicht unbedingt: `flex: 1` setzt `flex-basis: 0`,
                    der Kasten waechst also von Inhaltsbreite auf die volle
                    Restbreite. Das aendert die Umbruchbreite des
                    `titleAdornment`-`Stack` (`flexWrap: { xs: 'wrap' }`) auf
                    JEDEM Mobil-Kopf mit Status-Chip — ein zweiter, ungewollter
                    Eingriff. Hier ist er auf die Koepfe begrenzt, um die es
                    geht. */}
                <Box sx={{ minWidth: 0, ...(titelzeilenAktionen ? { flex: 1 } : {}) }}>
                  {mitTitelAdornment(titelKnotenMobil)}
                  {subtitle && (
                    React.isValidElement(subtitle) ? (
                      <Box sx={{ mt: 0.25 }}>{subtitle}</Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {subtitle}
                      </Typography>
                    )
                  )}
                </Box>
                {/* 🔴 Der Aktionsbereich in der Titelzeile, rechts vom Titel —
                    in der Hausreihenfolge `[sichtbare Aktionen] [⋯]`. Beide
                    Ausnahmen landen hier: der einsame ⋯ (F12) und die
                    ausschliesslich aus Symbolen bestehende Aktion (T3-3). */}
                {titelzeilenAktionen && (
                  <>
                    {visible.map(renderActionButton)}
                    {overflowMenu}
                  </>
                )}
              </Stack>

              {/* Actions row — wraps naturally. 🔴 Seit E5 traegt sie mobil
                  NUR die primaere Aktion plus den ⋯-Ueberlauf (Ausnahme
                  `stickyOnMobile`); vorher standen hier alle Aktionen inline.
                  🔴 R15 W6 / R16 W1 (Nachbesserung 2): hier stand
                  `justifyContent: onlyOverflow ? 'flex-end' : 'flex-start'`.
                  Der Zweig war TOT — die Zeile rendert nur, wenn
                  `hasActionsRow` gilt, und das schliesst `titelzeilenAktionen`
                  (also auch `onlyOverflow`) aus. Der Ausdruck war immer
                  `'flex-start'`, also die Vorgabe eines `Stack`; er ist
                  ersatzlos weg. Die Linksbuendigkeit, die der alte Kommentar
                  beschrieb, ist damit keine Zusicherung mehr, sondern der
                  Normalzustand. */}
              {hasActionsRow && (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  useFlexGap
                  alignItems="center"
                  sx={{ rowGap: 1 }}
                >
                  {reserveActionsRow ? (
                    // Leerer Platzhalter, KEIN Bedienelement: er haelt nur die
                    // Hoehe eines echten Knopfes. `width: 0`, damit er nichts
                    // sichtbar belegt; `aria-hidden`, damit er nicht in den
                    // Baum spricht.
                    <Box aria-hidden data-testid="viewheader-actions-pending" sx={{ ...CONTROL_HEIGHT_SX, width: 0 }} />
                  ) : (
                    <>
                      {/* E4: dieselbe Reihenfolge wie auf dem Desktop —
                          `[rightAdornment] [sekundaer …] [primaer] [⋯]`. */}
                      {rightAdornment}
                      {visible.map(renderActionButton)}
                      {overflowMenu}
                    </>
                  )}
                </Stack>
              )}
            </>
          ) : (
            /* ── Desktop: breadcrumbs + title left, actions right ── */
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* B3 (2026-10-06): der Rueckweg gehoert auf die SEITE, nicht in
                    die globale App-Leiste. Bis hierher rendete dieser Kopf den
                    `backLink` NUR mobil — auf dem Desktop kam er allein aus
                    `headerConfig.backLink` (`SidebarAndHeaderBar`). Wer ihn dort
                    entfernt (Produkt-Detail, Schritt 3.6), nimmt sonst den
                    einzigen Desktop-Rueckweg mit. Jetzt traegt der Desktop-Zweig
                    ihn ebenfalls: dieselbe Handlung, dieselbe Beschriftung,
                    ueber den Brotkruemen. */}
                {backLink && !backLinkStehtImKruem && (
                  <Button
                    startIcon={<ArrowBackIcon sx={{ fontSize: '1rem !important' }} />}
                    size="small"
                    variant="text"
                    onClick={goBack}
                    sx={{ px: 0, mb: 0.5, color: 'text.secondary', fontSize: '0.8rem', py: 0.25 }}
                  >
                    {backLink.label}
                  </Button>
                )}
                {!!breadcrumbs.length && (
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 0.5 }}>
                    {breadcrumbs.map((b, i) =>
                      // 🔴 W-1 (Wiedervorlage 1, 2026-10-07): der Kruem war ein
                      // MUI-`<Link>` OHNE `href` — die Navigation lief ueber
                      // `onClick`→`navigate`. Ein `<a>` ohne `href` ist NICHT
                      // fokussierbar und traegt KEINE `link`-Rolle; auf den zehn
                      // Seiten, auf denen die Desktop-Pille jetzt entfaellt
                      // (B3/W-1), war der Rueckweg damit nur noch mit der Maus
                      // erreichbar. Ein echter `RouterLink` traegt `href`, Rolle
                      // `link`, ist per Tab erreichbar und navigiert mit Enter.
                      // Ein Kruem, der NUR `onClick` mitbringt (Aufrufer, die
                      // ihr Ziel selbst bauen), bleibt klickbar — als
                      // `<button>`-Link, ebenfalls fokussierbar.
                      b.href ? (
                        <Link
                          key={i}
                          component={RouterLink}
                          to={b.href}
                          underline="hover"
                          color="inherit"
                        >
                          {b.label}
                        </Link>
                      ) : b.onClick ? (
                        <Link
                          key={i}
                          component="button"
                          type="button"
                          onClick={b.onClick}
                          underline="hover"
                          color="inherit"
                          sx={{ font: 'inherit', background: 'none', border: 0, p: 0, cursor: 'pointer' }}
                        >
                          {b.label}
                        </Link>
                      ) : (
                        <Typography key={i} color="text.secondary">{b.label}</Typography>
                      )
                    )}
                  </Breadcrumbs>
                )}
                {/* 🔴 F6 (UI-Durchgang 2, 2026-10-07): der Untertitel steht auf
                    dem DESKTOP in einem eigenen Block UNTER dem Titel — bis
                    hierher lag er in DERSELBEN `direction="row"`-Zeile wie der
                    Titel und wurde dort als erstes abgeschnitten. Der
                    Mobil-Zweig tat das schon immer (`:691-702`), der Desktop
                    nicht; der Fotobefund („Titel+Untertitel in derselben
                    Zeile") ist damit die eine Haelfte des Musters, die fehlte.
                    Der Aufbau ist jetzt woertlich derselbe wie mobil: ein
                    `Box`-Wrapper mit `minWidth: 0` traegt Titel UND Untertitel,
                    damit der Untertitel unter dem TITEL steht und nicht unter
                    der Kachel/dem Avatar. */}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  {headerAvatar}
                  {leftAdornment}
                  <Box sx={{ minWidth: 0 }}>
                    {mitTitelAdornment(titelKnotenDesktop)}
                    {subtitle && (
                      React.isValidElement(subtitle) ? (
                        <Box sx={{ mt: 0.25 }}>{subtitle}</Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                          {subtitle}
                        </Typography>
                      )
                    )}
                  </Box>
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ flexShrink: 0, ...KOPFZEILE_HOEHE }}
              >
                {rightAdornment}
                {visible.map(renderActionButton)}
                {overflowMenu}
              </Stack>
            </Stack>
          )}

          {/* Optional filter/chip row */}
          {children && (
            <Box sx={{ mt: 1 }}>
              {children}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}
